import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { getTierByProductId, StripeTierKey } from "@/lib/stripeTiers";

type UserRole = "master" | "professor" | "owner" | "nutricionista" | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: UserRole;
  tenantId: string | null;
  isStaff: boolean;
  isPlatformOwner: boolean;
  subscribed: boolean;
  subscriptionTier: StripeTierKey | null;
  subscriptionEnd: string | null;
  refreshSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  role: null,
  tenantId: null,
  isStaff: false,
  isPlatformOwner: false,
  subscribed: false,
  subscriptionTier: null,
  subscriptionEnd: null,
  refreshSubscription: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isStaff, setIsStaff] = useState(false);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<StripeTierKey | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const checkSubscription = useCallback(async (accessToken?: string | null) => {
    try {
      const token = accessToken ?? (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) return;

      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (error) {
        console.warn("[AuthContext] check-subscription error:", error.message);
        return;
      }
      setSubscribed(data?.subscribed ?? false);
      setSubscriptionTier(data?.product_id ? getTierByProductId(data.product_id) : null);
      setSubscriptionEnd(data?.subscription_end ?? null);
    } catch (err) {
      console.warn("[AuthContext] check-subscription exception:", err);
    }
  }, []);

  const fetchRole = async (userId: string) => {
    try {
      const { data: member, error: memberErr } = await supabase
        .from("tenant_members")
        .select("role, tenant_id")
        .eq("user_id", userId)
        .eq("status", "active")
        .maybeSingle();

      if (memberErr) {
        console.warn("[AuthContext] tenant_members query error:", memberErr.message);
      }

      if (member?.role) {
        setRole(member.role as UserRole);
        setTenantId(member.tenant_id);
        setIsStaff(true);
        setIsPlatformOwner(false);
        return;
      }

      const { data: owner, error: ownerErr } = await supabase
        .from("platform_owners")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (ownerErr) {
        console.warn("[AuthContext] platform_owners query error:", ownerErr.message);
      }

      if (owner) {
        setRole("owner");
        setTenantId(null);
        setIsStaff(true);
        setIsPlatformOwner(true);
        return;
      }

      setRole(null);
      setTenantId(null);
      setIsStaff(false);
      setIsPlatformOwner(false);
    } catch (err) {
      console.error("[AuthContext] fetchRole exception:", err);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        setLoading(true);

        if (!fetchingRef.current) {
          fetchingRef.current = true;
          setTimeout(() => {
            Promise.all([
              fetchRole(session.user.id),
              checkSubscription(session.access_token),
            ]).finally(() => {
              fetchingRef.current = false;
              setLoading(false);
            });
          }, 0);
        }
      } else {
        fetchingRef.current = false;
        setRole(null);
        setTenantId(null);
        setIsStaff(false);
        setIsPlatformOwner(false);
        setSubscribed(false);
        setSubscriptionTier(null);
        setSubscriptionEnd(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkSubscription]);

  // Auto-refresh subscription every 60 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setRole(null);
    setTenantId(null);
    setIsStaff(false);
    setIsPlatformOwner(false);
    setSubscribed(false);
    setSubscriptionTier(null);
    setSubscriptionEnd(null);
  };

  return (
    <AuthContext.Provider value={{
      user, session, loading, role, tenantId, isStaff, isPlatformOwner,
      subscribed, subscriptionTier, subscriptionEnd,
      refreshSubscription: checkSubscription,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
